import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { getSessionCourts, recordMatch } from "../api";

type Side = "a" | "b";
type PlayerPick = { user_id: string; name: string; initials: string; is_you?: boolean };

// Records one match result. Two modes:
//  - group-level: pass `players` (the group roster), format is selectable.
//  - session-level: pass `sessionId`; players come from that session's confirmed
//    attendees and the format is locked to the session's. The match is tied to
//    the session. Either way it's posted by an admin or a player who was in it,
//    and everyone who played is emailed.
export function RecordResultModal({
  groupId,
  players,
  sessionId,
  defaultFormat,
  onClose,
}: {
  groupId: string;
  players?: PlayerPick[];
  sessionId?: string;
  defaultFormat: "singles" | "doubles";
  onClose: (recorded: boolean) => void;
}) {
  const [format, setFormat] = useState<"singles" | "doubles">(defaultFormat);
  const [pool, setPool] = useState<PlayerPick[]>(players ?? []);
  const [loading, setLoading] = useState(Boolean(sessionId));
  const [assign, setAssign] = useState<Record<string, Side>>({});
  const [score, setScore] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Session mode: load the session's attendees and lock the format to it.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    getSessionCourts(sessionId)
      .then((layout) => {
        if (cancelled) return;
        const seated = layout.courts.flatMap((c) => c.players);
        setPool([...seated, ...layout.unassigned]);
        if (layout.format) setFormat(layout.format);
        setLoading(false);
      })
      .catch((e) => !cancelled && (setError(String(e?.message ?? e)), setLoading(false)));
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const perTeam = format === "singles" ? 1 : 2;
  const teamA = pool.filter((m) => assign[m.user_id] === "a");
  const teamB = pool.filter((m) => assign[m.user_id] === "b");
  const ready = teamA.length === perTeam && teamB.length === perTeam;
  const formatLocked = Boolean(sessionId);

  // Need two full teams for the chosen format (singles → 2, doubles → 4). When
  // there aren't enough players, block with a message rather than showing a
  // half-fillable form. (Empty pool is handled separately — invite reminder.)
  const minPlayers = perTeam * 2;
  const notEnough = pool.length > 0 && pool.length < minPlayers;
  const formatLabel = format === "singles" ? "Singles" : "Doubles";
  const tooFewMessage = formatLocked
    ? `Only ${pool.length} ${pool.length === 1 ? "player" : "players"} confirmed — not enough for a ${format} match.`
    : `${formatLabel} needs ${minPlayers} players — ${pool.length} ${pool.length === 1 ? "is" : "are"} here.` +
      (format === "doubles" && pool.length >= 2
        ? " Switch to singles, or invite more members."
        : " Invite more members to record a match.");

  function setSide(userId: string, side: Side) {
    setAssign((prev) => {
      const next = { ...prev };
      if (prev[userId] === side) delete next[userId]; // tap the active side to clear
      else next[userId] = side;
      return next;
    });
  }

  function setFmt(f: "singles" | "doubles") {
    setFormat(f);
    setAssign({}); // team sizes change → clear to avoid an over-full team
  }

  async function submit(winner: Side) {
    setBusy(true);
    setError(null);
    try {
      await recordMatch(groupId, {
        format,
        team_a: teamA.map((m) => m.user_id),
        team_b: teamB.map((m) => m.user_id),
        winner,
        session_id: sessionId ?? null,
        score: score.trim() || null,
      });
      onClose(true);
    } catch (e) {
      setError(String((e as Error)?.message ?? e));
      setBusy(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-ink/55 cs-modal-overlay" onClick={() => onClose(false)} />
      <div className="absolute bottom-0 left-0 right-0 cs-modal-card sm:top-1/2 sm:bottom-auto sm:-translate-y-1/2">
        <div className="max-w-lg mx-auto px-4 pb-4" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
          <div className="bg-bg border-2 border-ink rounded-t-xl sm:rounded-xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="px-5 py-3 bg-ink text-bg flex items-center justify-between shrink-0">
              <div className="font-mono text-[11px] uppercase tracking-[0.16em]">
                Record result{formatLocked ? ` · ${format}` : ""}
              </div>
              <button
                onClick={() => onClose(false)}
                className="text-bg/60 hover:text-bg font-mono text-2xl leading-none ml-3"
              >
                ×
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto">
              {error && (
                <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="text-ink/55 text-sm py-6 text-center">Loading players…</div>
              ) : pool.length === 0 ? (
                <div className="py-6 text-center">
                  <div className="font-display font-bold text-base mb-1">No players yet.</div>
                  <div className="text-sm text-ink/60">
                    Invite members to your club first — once they've played, you can
                    record results here.
                  </div>
                </div>
              ) : (
                <>
                  {/* Format toggle (group mode) + team counts. Keep the toggle even
                      when short of players so they can switch to a smaller format. */}
                  {(!formatLocked || !notEnough) && (
                    <div className="flex gap-2 mb-4 items-center">
                      {!formatLocked &&
                        (["singles", "doubles"] as const).map((f) => (
                          <button
                            key={f}
                            onClick={() => setFmt(f)}
                            className={
                              "px-3 py-1.5 font-display font-bold text-xs uppercase tracking-wider cs-tap " +
                              (format === f ? "bg-ink text-bg" : "border border-ink/20 hover:border-ink/40")
                            }
                          >
                            {f}
                          </button>
                        ))}
                      {!notEnough && (
                        <div className="ml-auto font-mono text-[11px] text-ink/55">
                          A {teamA.length}/{perTeam} · B {teamB.length}/{perTeam}
                        </div>
                      )}
                    </div>
                  )}

                  {notEnough ? (
                    <div className="border border-ink/15 rounded-md px-4 py-5 text-center text-sm text-ink/65">
                      {tooFewMessage}
                    </div>
                  ) : (
                    <>
                      <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-ink/55 mb-2">
                        Set each player's team
                      </div>
                      <div className="flex flex-col gap-1.5 mb-4">
                        {pool.map((m) => {
                          const side = assign[m.user_id];
                          return (
                            <div
                              key={m.user_id}
                              className={
                                "flex items-center gap-3 px-3 py-2 border rounded-md " +
                                (side ? "border-ink/40 bg-bg-2" : "border-ink/15")
                              }
                            >
                              <span className="w-7 h-7 rounded-full flex items-center justify-center font-display font-bold text-xs shrink-0 bg-bg-3 text-ink">
                                {m.initials}
                              </span>
                              <span className="flex-1 truncate text-sm">
                                {m.name}
                                {m.is_you && <span className="text-ink/40"> (you)</span>}
                              </span>
                              <div className="flex gap-1 shrink-0">
                                <button
                                  onClick={() => setSide(m.user_id, "a")}
                                  aria-pressed={side === "a"}
                                  className={
                                    "w-8 h-8 rounded font-display font-bold text-xs cs-tap border " +
                                    (side === "a"
                                      ? "bg-accent text-ink border-ink"
                                      : "border-ink/20 hover:border-ink/40 text-ink/60")
                                  }
                                >
                                  A
                                </button>
                                <button
                                  onClick={() => setSide(m.user_id, "b")}
                                  aria-pressed={side === "b"}
                                  className={
                                    "w-8 h-8 rounded font-display font-bold text-xs cs-tap border " +
                                    (side === "b"
                                      ? "bg-ink text-bg border-ink"
                                      : "border-ink/20 hover:border-ink/40 text-ink/60")
                                  }
                                >
                                  B
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <input
                        type="text"
                        value={score}
                        onChange={(e) => setScore(e.target.value)}
                        placeholder="Score (optional) — e.g. 6-4 7-5"
                        className="w-full mb-3 rounded-md border border-ink/20 px-3 py-2.5 text-sm focus:outline-none focus:border-ink/50 bg-bg"
                      />
                      <div className="flex gap-2">
                        <button
                          disabled={!ready || busy}
                          onClick={() => submit("a")}
                          className="flex-1 px-4 py-2.5 bg-accent text-ink font-display font-bold text-sm cs-tap disabled:opacity-40"
                        >
                          Team A won
                        </button>
                        <button
                          disabled={!ready || busy}
                          onClick={() => submit("b")}
                          className="flex-1 px-4 py-2.5 bg-ink text-bg font-display font-bold text-sm cs-tap disabled:opacity-40"
                        >
                          Team B won
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
